import { useEffect, useMemo, useState } from "react";
import {
  EmailAuthProvider,
  RecaptchaVerifier,
  createUserWithEmailAndPassword,
  linkWithCredential,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
  signOut,
  updatePassword,
} from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { auth, db, storage } from "./firebase";
import { MAJOR_INDIAN_CITIES, SERVICE_CATEGORIES } from "./serviceCatalog";
import { optimizeImageForUpload } from "./imageUploadUtils";
import { assertStorageReady, formatUploadError } from "./uploadErrorUtils";
import { PageHeader, ToneBadge } from "./petaarUi";

const EMAIL_PENDING_KEY = "petaar_signup_email_pending_v1";
const ID_PROOF_OPTIONS = [
  { value: "aadhaar", label: "Aadhaar Card" },
  { value: "driving_license", label: "Driver License" },
  { value: "passport", label: "Passport" },
];
const EXPERTISE_OPTIONS = [
  { value: "dog", label: "Dog" },
  { value: "cat", label: "Cat" },
  { value: "both", label: "Both" },
];
const MAX_PROFILE_PHOTO_BYTES = 10 * 1024 * 1024;
const MAX_SERVICE_PHOTO_BYTES = 12 * 1024 * 1024;
const MAX_SERVICE_PHOTOS = 6;
const INDIAN_PINCODE_REGEX = /^\d{6}$/;

function makeTempPassword() {
  return `P3taar!${Math.random().toString(36).slice(2, 10)}${Date.now().toString().slice(-4)}`;
}

function readPendingEmailState() {
  try {
    const raw = sessionStorage.getItem(EMAIL_PENDING_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== "object") return null;
    if (!parsed.email || !parsed.tempPassword) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

function writePendingEmailState(email, tempPassword) {
  try {
    sessionStorage.setItem(
      EMAIL_PENDING_KEY,
      JSON.stringify({
        email,
        tempPassword,
        updatedAt: Date.now(),
      }),
    );
  } catch (_) {}
}

function clearPendingEmailState() {
  try {
    sessionStorage.removeItem(EMAIL_PENDING_KEY);
  } catch (_) {}
}

function toSafeFileName(name) {
  return String(name || "file")
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, "-")
    .slice(0, 80);
}

function parseCsvInput(value) {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function normalizePincode(value) {
  return String(value || "").replace(/\D/g, "").slice(0, 6);
}

function looksLikeValidPincode(value) {
  return INDIAN_PINCODE_REGEX.test(String(value || "").trim());
}

function buildAddressString(address = {}) {
  const line1 = String(address.line1 || "").trim();
  const city = String(address.city || "").trim();
  const pincode = String(address.pincode || "").trim();
  const state = String(address.state || "").trim();
  return [line1, city, pincode, state].filter(Boolean).join(", ");
}

function looksLikeValidEmail(value) {
  const text = String(value || "").trim();
  if (!text) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text);
}

function normalizePhone(value) {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("+")) return trimmed;
  return `+91${trimmed.replace(/\D/g, "")}`;
}

function looksLikeValidIndianPhone(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 10) return true;
  return digits.length === 12 && digits.startsWith("91");
}

function buildContactVerificationPayload(payload = {}) {
  const method = String(payload.method || "").trim().toLowerCase();
  const allowedMethod = method === "phone" ? "phone" : "email";
  const emailVerified = Boolean(payload.emailVerified);
  const phoneVerified = Boolean(payload.phoneVerified);
  return {
    method: allowedMethod,
    emailVerified,
    phoneVerified,
    verifiedAt: serverTimestamp(),
  };
}

export default function Signup({ goLogin, initialRole = "owner" }) {
  const bubbleLabelStyle = {
    display: "block",
    fontWeight: 700,
    color: "var(--text)",
    marginBottom: 8,
    fontSize: 14,
    letterSpacing: 0.2,
  };
  const bubbleInputStyle = {
    width: "100%",
    borderRadius: 999,
    border: "1px solid var(--border)",
    background: "#ffffff",
    color: "var(--text)",
    padding: "14px 18px",
    fontSize: 16,
    outline: "none",
    boxSizing: "border-box",
    boxShadow: "inset 0 1px 2px rgba(15, 23, 42, 0.04)",
  };
  const bubbleSelectStyle = {
    ...bubbleInputStyle,
    paddingRight: 34,
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
    backgroundImage:
      "linear-gradient(45deg, transparent 50%, #2563eb 50%), linear-gradient(135deg, #2563eb 50%, transparent 50%), linear-gradient(to right, transparent, transparent)",
    backgroundPosition: "calc(100% - 20px) calc(1em + 4px), calc(100% - 14px) calc(1em + 4px), 100% 0",
    backgroundSize: "6px 6px, 6px 6px, 2.5em 2.5em",
    backgroundRepeat: "no-repeat",
  };
  const bubbleTextareaStyle = {
    ...bubbleInputStyle,
    minHeight: 110,
    resize: "vertical",
    fontFamily: "inherit",
    borderRadius: 20,
  };
  const bubblePrimaryBtn = {
    borderRadius: 999,
    border: "none",
    background: "#7f81a7",
    color: "#ffffff",
    fontWeight: 800,
    fontSize: 17,
    letterSpacing: 0.2,
    padding: "14px 18px",
    width: "min(100%, 260px)",
    cursor: "pointer",
    boxShadow: "0 14px 28px rgba(82, 90, 136, 0.22)",
  };
  const bubbleSecondaryBtn = {
    borderRadius: 999,
    border: "1px solid var(--border)",
    background: "#ffffff",
    color: "var(--text)",
    fontWeight: 700,
    padding: "12px 18px",
    minWidth: 0,
    cursor: "pointer",
  };
  const authCardStyle = {
    background: "linear-gradient(180deg, rgba(255,255,255,0.97) 0%, rgba(238,238,255,0.88) 100%)",
    border: "1px solid rgba(26, 26, 219, 0.08)",
  };
  const helperCardStyle = {
    background: "linear-gradient(180deg, rgba(245,166,35,0.12) 0%, rgba(255,255,255,0.96) 100%)",
    border: "1px solid rgba(245, 166, 35, 0.22)",
    display: "grid",
    gap: 16,
    alignContent: "start",
  };
  const sectionPanelStyle = {
    border: "1px solid var(--border)",
    borderRadius: 22,
    padding: 18,
    background: "rgba(255,255,255,0.84)",
  };

  const getAuthErrorMessage = (error) => {
    const code = String(error?.code || "");
    const fallback = String(error?.message || "Something went wrong. Please try again.");
    const map = {
      "auth/email-already-in-use": "This email is already registered. Please login.",
      "auth/weak-password": "Password is too weak. Use at least 6 characters.",
      "auth/invalid-email": "Please enter a valid email address.",
      "auth/operation-not-allowed": "This signup method is disabled in Firebase Auth settings.",
      "auth/invalid-phone-number": "Invalid phone number format. Use +91XXXXXXXXXX.",
      "auth/invalid-verification-code": "Incorrect OTP. Please check and try again.",
      "auth/code-expired": "OTP expired. Please request a new OTP.",
      "auth/invalid-app-credential": "App verification failed. Use 127.0.0.1 or refresh and try again.",
      "auth/captcha-check-failed": "OTP setup failed: domain is not authorized. Add petaar.com and www.petaar.com in Firebase Auth > Settings > Authorized domains.",
      "auth/unauthorized-domain": "This domain is not authorized for OTP login. Add petaar.com and www.petaar.com in Firebase Auth > Settings.",
      "auth/quota-exceeded": "SMS quota exceeded. Please try later.",
      "auth/too-many-requests": "Too many attempts. Please try again after some time.",
      "auth/credential-already-in-use": "This contact credential is already linked to another account.",
      "auth/provider-already-linked": "Email/password is already linked for this account.",
      "auth/requires-recent-login": "Please verify again and retry password setup.",
    };
    return map[code] || fallback;
  };

  const [mode, setMode] = useState("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [role, setRole] = useState(initialRole === "provider" ? "provider" : "owner");

  const [busy, setBusy] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailTempPassword, setEmailTempPassword] = useState("");

  const [providerStep, setProviderStep] = useState(1);
  const [partnerName, setPartnerName] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [providerCity, setProviderCity] = useState("");
  const [providerPrimaryCategory, setProviderPrimaryCategory] = useState("");

  const [panCardNumber, setPanCardNumber] = useState("");
  const [idProofType, setIdProofType] = useState("");
  const [idProofNumber, setIdProofNumber] = useState("");
  const [currentAddressLine, setCurrentAddressLine] = useState("");
  const [currentAddressCity, setCurrentAddressCity] = useState("");
  const [currentAddressPincode, setCurrentAddressPincode] = useState("");
  const [currentAddressState, setCurrentAddressState] = useState("");
  const [currentAddressLookupBusy, setCurrentAddressLookupBusy] = useState(false);
  const [currentAddressLookupError, setCurrentAddressLookupError] = useState("");
  const [permanentAddressLine, setPermanentAddressLine] = useState("");
  const [permanentAddressCity, setPermanentAddressCity] = useState("");
  const [permanentAddressPincode, setPermanentAddressPincode] = useState("");
  const [permanentAddressState, setPermanentAddressState] = useState("");
  const [permanentAddressLookupBusy, setPermanentAddressLookupBusy] = useState(false);
  const [permanentAddressLookupError, setPermanentAddressLookupError] = useState("");
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [panCardPhotoFile, setPanCardPhotoFile] = useState(null);
  const [idProofPhotoFile, setIdProofPhotoFile] = useState(null);

  const [serviceCategory, setServiceCategory] = useState("");
  const [serviceExperience, setServiceExperience] = useState("");
  const [serviceExpertise, setServiceExpertise] = useState("both");
  const [serviceLanguages, setServiceLanguages] = useState("");
  const [serviceAbout, setServiceAbout] = useState("");
  const [servicePhotoFiles, setServicePhotoFiles] = useState([]);

  const isProvider = role === "provider";
  const isLocalhost = typeof window !== "undefined" && window.location.hostname === "localhost";
  const normalizedEmail = useMemo(() => String(email || "").trim().toLowerCase(), [email]);
  const normalizedPhone = useMemo(() => normalizePhone(phone), [phone]);

  const setupRecaptcha = () => {
    const container = document.getElementById("signup-recaptcha");
    if (!container) {
      throw new Error("reCAPTCHA container not found. Please refresh and try again.");
    }
    if (window.signupRecaptchaVerifier) {
      return window.signupRecaptchaVerifier;
    }
    window.signupRecaptchaVerifier = new RecaptchaVerifier(auth, "signup-recaptcha", {
      size: "invisible",
    });
    return window.signupRecaptchaVerifier;
  };

  const clearRecaptcha = () => {
    if (window.signupRecaptchaVerifier) {
      try {
        window.signupRecaptchaVerifier.clear();
      } catch (_) {}
      window.signupRecaptchaVerifier = null;
    }
  };

  const open127Host = () => {
    if (typeof window === "undefined") return;
    const { protocol, port, pathname, search, hash } = window.location;
    window.location.assign(`${protocol}//127.0.0.1${port ? `:${port}` : ""}${pathname}${search}${hash}`);
  };

  const resetEmailVerificationState = ({ clearEmail = false } = {}) => {
    setEmailVerificationSent(false);
    setEmailVerified(false);
    setEmailTempPassword("");
    if (clearEmail) {
      setEmail("");
    }
    clearPendingEmailState();
  };

  const clearVerificationStateForModeSwitch = () => {
    setConfirmationResult(null);
    setOtp("");
    setPassword("");
    resetEmailVerificationState();
  };

  useEffect(() => () => {
    clearRecaptcha();
  }, []);

  useEffect(() => {
    const pending = readPendingEmailState();
    if (!pending) return;
    setMode("email");
    setEmail(pending.email);
    setEmailTempPassword(pending.tempPassword);
    setEmailVerificationSent(true);
    setEmailVerified(false);
  }, []);

  useEffect(() => {
    const nextRole = initialRole === "provider" ? "provider" : "owner";
    setRole(nextRole);
  }, [initialRole]);

  useEffect(() => {
    if (mode !== "phone") {
      clearRecaptcha();
      setConfirmationResult(null);
      setOtp("");
    }
  }, [mode]);

  useEffect(() => {
    if (!isProvider) {
      setProviderStep(1);
      return;
    }
    if (providerPrimaryCategory && !serviceCategory) {
      setServiceCategory(providerPrimaryCategory);
    }
  }, [isProvider, providerPrimaryCategory, serviceCategory]);

  useEffect(() => {
    let cancelled = false;
    const runLookup = async () => {
      if (!looksLikeValidPincode(currentAddressPincode)) {
        setCurrentAddressState("");
        setCurrentAddressLookupError("");
        setCurrentAddressLookupBusy(false);
        return;
      }
      setCurrentAddressLookupBusy(true);
      setCurrentAddressLookupError("");
      try {
        const response = await fetch(`https://api.postalpincode.in/pincode/${currentAddressPincode}`);
        const payload = await response.json();
        const first = Array.isArray(payload) ? payload[0] : null;
        const postOffice = Array.isArray(first?.PostOffice) ? first.PostOffice[0] : null;
        const nextState = String(postOffice?.State || "").trim();
        if (cancelled) return;
        if (!nextState) {
          setCurrentAddressState("");
          setCurrentAddressLookupError("Could not detect state for this pincode.");
          return;
        }
        setCurrentAddressState(nextState);
      } catch (_) {
        if (!cancelled) {
          setCurrentAddressState("");
          setCurrentAddressLookupError("Could not detect state right now.");
        }
      } finally {
        if (!cancelled) {
          setCurrentAddressLookupBusy(false);
        }
      }
    };
    void runLookup();
    return () => {
      cancelled = true;
    };
  }, [currentAddressPincode]);

  useEffect(() => {
    let cancelled = false;
    const runLookup = async () => {
      if (!looksLikeValidPincode(permanentAddressPincode)) {
        setPermanentAddressState("");
        setPermanentAddressLookupError("");
        setPermanentAddressLookupBusy(false);
        return;
      }
      setPermanentAddressLookupBusy(true);
      setPermanentAddressLookupError("");
      try {
        const response = await fetch(`https://api.postalpincode.in/pincode/${permanentAddressPincode}`);
        const payload = await response.json();
        const first = Array.isArray(payload) ? payload[0] : null;
        const postOffice = Array.isArray(first?.PostOffice) ? first.PostOffice[0] : null;
        const nextState = String(postOffice?.State || "").trim();
        if (cancelled) return;
        if (!nextState) {
          setPermanentAddressState("");
          setPermanentAddressLookupError("Could not detect state for this pincode.");
          return;
        }
        setPermanentAddressState(nextState);
      } catch (_) {
        if (!cancelled) {
          setPermanentAddressState("");
          setPermanentAddressLookupError("Could not detect state right now.");
        }
      } finally {
        if (!cancelled) {
          setPermanentAddressLookupBusy(false);
        }
      }
    };
    void runLookup();
    return () => {
      cancelled = true;
    };
  }, [permanentAddressPincode]);

  const validateProviderStep1 = () => {
    if (!String(partnerName || "").trim()) {
      alert("Please enter your full name.");
      return false;
    }
    if (!looksLikeValidEmail(normalizedEmail)) {
      alert("Please enter a valid email.");
      return false;
    }
    if (!looksLikeValidIndianPhone(phone)) {
      alert("Please enter a valid mobile number.");
      return false;
    }
    if (!providerPrimaryCategory) {
      alert("Please select what service you provide.");
      return false;
    }
    if (!termsAccepted) {
      alert("Please accept terms to continue.");
      return false;
    }
    return true;
  };

  const validateProviderStep2 = () => {
    if (!providerCity) {
      alert("Please select your city.");
      return false;
    }
    if (!String(currentAddressLine || "").trim()) {
      alert("Please enter current address line.");
      return false;
    }
    if (!String(currentAddressCity || "").trim()) {
      alert("Please enter current city.");
      return false;
    }
    if (!looksLikeValidPincode(currentAddressPincode)) {
      alert("Please enter valid current pincode.");
      return false;
    }
    if (!String(currentAddressState || "").trim()) {
      alert("Current state could not be detected. Check pincode.");
      return false;
    }
    if (!String(permanentAddressLine || "").trim()) {
      alert("Please enter permanent address line.");
      return false;
    }
    if (!String(permanentAddressCity || "").trim()) {
      alert("Please enter permanent city.");
      return false;
    }
    if (!looksLikeValidPincode(permanentAddressPincode)) {
      alert("Please enter valid permanent pincode.");
      return false;
    }
    if (!String(permanentAddressState || "").trim()) {
      alert("Permanent state could not be detected. Check pincode.");
      return false;
    }
    if (!String(panCardNumber || "").trim()) {
      alert("Please enter PAN card number.");
      return false;
    }
    if (!idProofType) {
      alert("Please select ID proof type.");
      return false;
    }
    if (!String(idProofNumber || "").trim()) {
      alert("Please enter ID proof number.");
      return false;
    }
    if (!profilePhotoFile) {
      alert("Please upload profile photo.");
      return false;
    }
    if (!panCardPhotoFile) {
      alert("Please upload PAN card photo.");
      return false;
    }
    if (!idProofPhotoFile) {
      alert("Please upload ID proof photo.");
      return false;
    }
    return true;
  };

  const validateProviderStep3 = () => {
    if (!serviceCategory) {
      alert("Please select service category.");
      return false;
    }
    if (serviceExperience === "" || Number(serviceExperience) < 0) {
      alert("Please enter valid experience in years.");
      return false;
    }
    if (!serviceExpertise) {
      alert("Please select expertise.");
      return false;
    }
    if (!String(serviceLanguages || "").trim()) {
      alert("Please enter language details.");
      return false;
    }
    if (!String(serviceAbout || "").trim()) {
      alert("Please enter something about yourself.");
      return false;
    }
    if (!servicePhotoFiles.length) {
      alert("Please upload at least one service photo.");
      return false;
    }
    return true;
  };

  const ensureProviderReadyForSubmit = () =>
    validateProviderStep1() && validateProviderStep2() && validateProviderStep3();

  const nextProviderStep = () => {
    if (providerStep === 1 && !validateProviderStep1()) return;
    if (providerStep === 2 && !validateProviderStep2()) return;
    setProviderStep((prev) => Math.min(3, prev + 1));
  };

  const previousProviderStep = () => {
    setProviderStep((prev) => Math.max(1, prev - 1));
  };

  const onProfilePhotoChange = (file) => {
    if (!file) return;
    if (!String(file.type || "").toLowerCase().startsWith("image/")) {
      alert("Profile photo must be an image.");
      return;
    }
    if (Number(file.size || 0) > MAX_PROFILE_PHOTO_BYTES) {
      alert("Profile photo must be under 10MB.");
      return;
    }
    setProfilePhotoFile(file);
  };

  const onDocumentPhotoChange = (file, onAccept) => {
    if (!file) return;
    if (!String(file.type || "").toLowerCase().startsWith("image/")) {
      alert("Document photo must be an image.");
      return;
    }
    if (Number(file.size || 0) > MAX_PROFILE_PHOTO_BYTES) {
      alert("Document photo must be under 10MB.");
      return;
    }
    onAccept(file);
  };

  const onServicePhotosChange = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const valid = files.filter((file) => (
      String(file.type || "").toLowerCase().startsWith("image/") &&
      Number(file.size || 0) <= MAX_SERVICE_PHOTO_BYTES
    ));
    if (!valid.length) {
      alert("Please select image files under 12MB.");
      return;
    }
    if (valid.length < files.length) {
      alert("Some files were skipped (only images under 12MB are allowed).");
    }
    setServicePhotoFiles((prev) => [...prev, ...valid].slice(0, MAX_SERVICE_PHOTOS));
  };

  const removeServicePhotoAt = (index) => {
    setServicePhotoFiles((prev) => prev.filter((_, idx) => idx !== index));
  };

  const uploadPartnerImage = async (uid, file, prefix = "photo", maxOutputBytes = 900 * 1024) => {
    if (!file) return "";
    await assertStorageReady(storage?.app?.options?.storageBucket || "");
    const uploadFile = await optimizeImageForUpload(file, {
      maxWidth: 1800,
      maxHeight: 1800,
      quality: 0.82,
      maxOutputBytes,
      skipBelowBytes: 220 * 1024,
      outputType: "image/jpeg",
    });
    const safeName = toSafeFileName(uploadFile.name || file.name || "photo.jpg");
    const objectPath = `partnerOnboarding/${uid}/${Date.now()}-${prefix}-${Math.random().toString(36).slice(2, 8)}-${safeName}`;
    const fileRef = ref(storage, objectPath);
    await uploadBytes(fileRef, uploadFile, {
      contentType: uploadFile.type || file.type || "application/octet-stream",
      cacheControl: "private,max-age=0,no-store",
    });
    return getDownloadURL(fileRef);
  };

  const uploadProviderMedia = async (uid) => {
    if (!isProvider) {
      return { profilePhotoUrl: "", panCardPhotoUrl: "", idProofPhotoUrl: "", servicePhotoUrls: [] };
    }
    const profilePhotoUrl = await uploadPartnerImage(uid, profilePhotoFile, "profile", 650 * 1024);
    const panCardPhotoUrl = await uploadPartnerImage(uid, panCardPhotoFile, "pan-card", 850 * 1024);
    const idProofPhotoUrl = await uploadPartnerImage(uid, idProofPhotoFile, "id-proof", 850 * 1024);
    const servicePhotoUrls = [];
    for (let idx = 0; idx < servicePhotoFiles.length; idx += 1) {
      const file = servicePhotoFiles[idx];
      const url = await uploadPartnerImage(uid, file, `service-${idx + 1}`, 980 * 1024);
      servicePhotoUrls.push(url);
    }
    return { profilePhotoUrl, panCardPhotoUrl, idProofPhotoUrl, servicePhotoUrls };
  };

  const buildProviderSubmission = ({ profilePhotoUrl, panCardPhotoUrl, idProofPhotoUrl, servicePhotoUrls }) => {
    const languages = parseCsvInput(serviceLanguages);
    const timestamp = new Date().toISOString();
    const currentAddress = {
      line1: String(currentAddressLine || "").trim(),
      city: String(currentAddressCity || "").trim(),
      pincode: normalizePincode(currentAddressPincode),
      state: String(currentAddressState || "").trim(),
    };
    const permanentAddress = {
      line1: String(permanentAddressLine || "").trim(),
      city: String(permanentAddressCity || "").trim(),
      pincode: normalizePincode(permanentAddressPincode),
      state: String(permanentAddressState || "").trim(),
    };
    return {
      partnerApplication: {
        providerName: partnerName.trim(),
        providerEmail: normalizedEmail,
        providerPhone: normalizedPhone,
        city: providerCity,
        primaryServiceCategory: providerPrimaryCategory,
        personalDetails: {
          panCardNumber: String(panCardNumber || "").trim().toUpperCase(),
          panCardPhotoUrl,
          idProofType,
          idProofNumber: String(idProofNumber || "").trim(),
          idProofPhotoUrl,
          currentAddress,
          permanentAddress,
          currentAddressText: buildAddressString(currentAddress),
          permanentAddressText: buildAddressString(permanentAddress),
          profilePhotoUrl,
        },
        serviceDetails: {
          category: serviceCategory,
          yearsOfExperience: Number(serviceExperience) || 0,
          expertise: serviceExpertise,
          languages,
          about: String(serviceAbout || "").trim(),
          servicePhotoUrls,
        },
        status: "pending_review",
        submittedAt: timestamp,
        updatedAt: timestamp,
      },
      userMergeFields: {
        displayName: partnerName.trim(),
        businessName: partnerName.trim(),
        about: String(serviceAbout || "").trim(),
        city: providerCity,
        primaryCategory: providerPrimaryCategory,
        yearsExperience: String(serviceExperience || "").trim(),
        providerStatus: "pending",
        partnerOnboardingStatus: "pending_review",
        partnerOnboardingUpdatedAt: serverTimestamp(),
        partnerProfile: {
          personalDetails: {
            panCardNumber: String(panCardNumber || "").trim().toUpperCase(),
            panCardPhotoUrl,
            idProofType,
            idProofNumber: String(idProofNumber || "").trim(),
            idProofPhotoUrl,
            currentAddress,
            permanentAddress,
            currentAddressText: buildAddressString(currentAddress),
            permanentAddressText: buildAddressString(permanentAddress),
            profilePhotoUrl,
          },
          serviceDetails: {
            category: serviceCategory,
            yearsOfExperience: Number(serviceExperience) || 0,
            expertise: serviceExpertise,
            languages,
            about: String(serviceAbout || "").trim(),
            servicePhotoUrls,
          },
        },
      },
    };
  };

  const saveUserDoc = async (firebaseUser, profile, verificationPayload = null) => {
    const userRef = doc(db, "users", firebaseUser.uid);
    const existing = await getDoc(userRef);
    const verificationMerge =
      verificationPayload && typeof verificationPayload === "object" ?
        { contactVerification: verificationPayload } :
        {};
    if (existing.exists()) {
      const current = existing.data() || {};
      if (current.role === "owner" && profile.role === "provider") {
        await setDoc(
          userRef,
          {
            ...profile,
            role: "provider",
            providerStatus: "pending",
            email: profile.email || current.email || firebaseUser.email || "",
            phone: profile.phone || current.phone || firebaseUser.phoneNumber || "",
            ...verificationMerge,
          },
          { merge: true },
        );
        return "upgraded";
      }
      await setDoc(userRef, { ...profile, ...verificationMerge }, { merge: true });
      return "existing";
    }
    await setDoc(userRef, { ...profile, ...verificationMerge }, { merge: true });
    return "created";
  };

  const buildBaseProfile = (firebaseUser, extraFields = {}) => ({
    email: normalizedEmail || firebaseUser.email || "",
    phone: normalizedPhone || firebaseUser.phoneNumber || "",
    role,
    city: isProvider ? providerCity : "",
    primaryCategory: isProvider ? providerPrimaryCategory : "",
    providerStatus: isProvider ? "pending" : "",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    ...extraFields,
  });

  const sendEmailOtp = async () => {
    if (!looksLikeValidEmail(normalizedEmail)) {
      alert("Please enter a valid email.");
      return;
    }
    if (isProvider && !ensureProviderReadyForSubmit()) return;
    try {
      setBusy(true);
      if (auth.currentUser) {
        await signOut(auth).catch(() => {});
      }
      const tempPassword = makeTempPassword();
      const userCred = await createUserWithEmailAndPassword(auth, normalizedEmail, tempPassword);
      await sendEmailVerification(userCred.user);
      await signOut(auth);
      setEmailTempPassword(tempPassword);
      setEmailVerificationSent(true);
      setEmailVerified(false);
      setPassword("");
      writePendingEmailState(normalizedEmail, tempPassword);
      alert("Verification OTP/link sent to your email. Verify first, then set password.");
    } catch (error) {
      alert(getAuthErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const resendEmailOtp = async () => {
    if (!normalizedEmail || !emailTempPassword) {
      alert("Start email verification again.");
      return;
    }
    try {
      setBusy(true);
      const userCred = await signInWithEmailAndPassword(auth, normalizedEmail, emailTempPassword);
      await sendEmailVerification(userCred.user);
      await signOut(auth);
      alert("Verification OTP/link resent to your email.");
    } catch (error) {
      alert(getAuthErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const checkEmailVerification = async () => {
    if (!normalizedEmail || !emailTempPassword) {
      alert("Start email verification first.");
      return;
    }
    try {
      setBusy(true);
      const userCred = await signInWithEmailAndPassword(auth, normalizedEmail, emailTempPassword);
      await userCred.user.reload();
      const verified = Boolean(userCred.user.emailVerified);
      await signOut(auth);
      if (!verified) {
        setEmailVerified(false);
        alert("Email is not verified yet. Please verify and try again.");
        return;
      }
      setEmailVerified(true);
      alert("Email verified successfully. Now set your password.");
    } catch (error) {
      alert(getAuthErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const completeEmailSignup = async () => {
    if (isProvider && !ensureProviderReadyForSubmit()) return;
    if (!looksLikeValidEmail(normalizedEmail) || !emailTempPassword || !emailVerificationSent) {
      alert("Please complete email verification first.");
      return;
    }
    if (!emailVerified) {
      alert("Please verify your email before setting password.");
      return;
    }
    if (String(password || "").length < 6) {
      alert("Password must be at least 6 characters.");
      return;
    }
    try {
      setBusy(true);
      const userCred = await signInWithEmailAndPassword(auth, normalizedEmail, emailTempPassword);
      await userCred.user.reload();
      if (!userCred.user.emailVerified) {
        await signOut(auth);
        setEmailVerified(false);
        alert("Email verification is still pending.");
        return;
      }
      await updatePassword(userCred.user, password);

      let mergedExtraFields = {};
      if (isProvider) {
        const uploaded = await uploadProviderMedia(userCred.user.uid);
        const providerSubmission = buildProviderSubmission(uploaded);
        mergedExtraFields = providerSubmission.userMergeFields;
        await setDoc(doc(db, "partnerApplications", userCred.user.uid), {
          providerId: userCred.user.uid,
          providerEmail: normalizedEmail,
          ...providerSubmission.partnerApplication,
          submittedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }, { merge: true });
      }

      const status = await saveUserDoc(
        userCred.user,
        buildBaseProfile(userCred.user, mergedExtraFields),
        buildContactVerificationPayload({
          method: "email",
          emailVerified: true,
          phoneVerified: Boolean(userCred.user.phoneNumber),
        }),
      );

      await signOut(auth);
      clearPendingEmailState();
      resetEmailVerificationState();
      if (status === "upgraded") {
        alert("Account upgraded to service provider. Please login.");
      } else if (status === "created") {
        alert(isProvider ?
          "Partner details submitted. We will verify and update you soon." :
          "Signup completed successfully. Please login.");
      } else {
        alert("Account already exists. Please login.");
      }
      goLogin();
    } catch (error) {
      alert(isProvider ? formatUploadError(error, storage?.app?.options?.storageBucket) : getAuthErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const sendPhoneOtp = async () => {
    if (!looksLikeValidIndianPhone(phone)) {
      alert("Please enter a valid mobile number.");
      return;
    }
    if (isProvider && !ensureProviderReadyForSubmit()) return;
    try {
      setSendingOtp(true);
      if (auth.currentUser) {
        await signOut(auth).catch(() => {});
      }
      const verifier = setupRecaptcha();
      await verifier.render();
      const result = await signInWithPhoneNumber(auth, normalizedPhone, verifier);
      setConfirmationResult(result);
      setPhone(normalizedPhone);
      alert("OTP sent to your mobile number.");
    } catch (error) {
      alert(getAuthErrorMessage(error));
    } finally {
      setSendingOtp(false);
    }
  };

  const completePhoneSignup = async () => {
    if (!confirmationResult) {
      alert("Please request OTP first.");
      return;
    }
    if (!looksLikeValidEmail(normalizedEmail)) {
      alert("Email is required to set password after phone verification.");
      return;
    }
    if (isProvider && !ensureProviderReadyForSubmit()) return;
    if (String(password || "").length < 6) {
      alert("Password must be at least 6 characters.");
      return;
    }
    try {
      setBusy(true);
      const userCred = await confirmationResult.confirm(otp);
      let didLinkEmailCredential = false;
      try {
        const emailCredential = EmailAuthProvider.credential(normalizedEmail, password);
        await linkWithCredential(userCred.user, emailCredential);
        didLinkEmailCredential = true;
      } catch (error) {
        const code = String(error?.code || "");
        if (code === "auth/provider-already-linked") {
          const linkedEmail = String(userCred.user.email || "").toLowerCase();
          if (linkedEmail && linkedEmail !== normalizedEmail) {
            throw new Error("This mobile number is already linked with another email account.");
          }
        } else {
          throw error;
        }
      }

      if (!didLinkEmailCredential) {
        await updatePassword(userCred.user, password);
      }

      let mergedExtraFields = {};
      if (isProvider) {
        const uploaded = await uploadProviderMedia(userCred.user.uid);
        const providerSubmission = buildProviderSubmission(uploaded);
        mergedExtraFields = providerSubmission.userMergeFields;
        await setDoc(doc(db, "partnerApplications", userCred.user.uid), {
          providerId: userCred.user.uid,
          providerEmail: normalizedEmail,
          ...providerSubmission.partnerApplication,
          submittedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }, { merge: true });
      }

      const status = await saveUserDoc(
        userCred.user,
        buildBaseProfile(userCred.user, mergedExtraFields),
        buildContactVerificationPayload({
          method: "phone",
          emailVerified: Boolean(userCred.user.emailVerified),
          phoneVerified: true,
        }),
      );

      await signOut(auth);
      setConfirmationResult(null);
      setOtp("");
      if (status === "upgraded") {
        alert("Account upgraded to service provider. Please login.");
      } else if (status === "created") {
        alert(isProvider ?
          "Partner details submitted. We will verify and update you soon." :
          "Signup completed successfully. Please login.");
      } else {
        alert("Account already exists. Please login.");
      }
      goLogin();
    } catch (error) {
      alert(isProvider ? formatUploadError(error, storage?.app?.options?.storageBucket) : getAuthErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const renderVerificationBlock = () => {
    const methodPillStyle = (active) => ({
      ...bubbleSecondaryBtn,
      minWidth: 0,
      borderColor: active ? "#3b82f6" : "#cbd5e1",
      background: active ? "#dbeafe" : "#ffffff",
      color: active ? "#1e3a8a" : "#475569",
    });

    return (
      <div className="petaar-card" style={{ marginTop: 22, ...authCardStyle }}>
        <div style={{ fontWeight: 800, color: "#374151", fontSize: 22, marginBottom: 10 }}>
          Verify Contact & Set Password
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
          <button
            onClick={() => {
              if (mode !== "email") {
                setMode("email");
                clearVerificationStateForModeSwitch();
              }
            }}
            disabled={busy}
            style={methodPillStyle(mode === "email")}
          >
            Verify with Email
          </button>
          <button
            onClick={() => {
              if (mode !== "phone") {
                setMode("phone");
                clearVerificationStateForModeSwitch();
              }
            }}
            disabled={busy}
            style={methodPillStyle(mode === "phone")}
          >
            Verify with Phone OTP
          </button>
        </div>

        {mode === "email" ? (
          <div style={{ display: "grid", gap: 10, maxWidth: 640 }}>
            <input
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy || emailVerificationSent}
              style={bubbleInputStyle}
            />
            {!emailVerificationSent ? (
              <button
                onClick={sendEmailOtp}
                disabled={busy || !normalizedEmail}
                style={{ ...bubblePrimaryBtn, width: "min(100%, 220px)" }}
              >
                {busy ? "Sending..." : "Send Email OTP"}
              </button>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                <small style={{ color: "#475569", fontSize: 15 }}>
                  OTP/link sent to <strong>{normalizedEmail}</strong>. Verify your email first.
                </small>
                {!emailVerified && (
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button
                      onClick={checkEmailVerification}
                      disabled={busy}
                      style={{ ...bubblePrimaryBtn, width: "min(100%, 180px)" }}
                    >
                      {busy ? "Checking..." : "I Verified Email"}
                    </button>
                    <button
                      onClick={resendEmailOtp}
                      disabled={busy}
                      style={{ ...bubbleSecondaryBtn, minWidth: 0 }}
                    >
                      Resend OTP
                    </button>
                    <button
                      onClick={() => resetEmailVerificationState({ clearEmail: true })}
                      disabled={busy}
                      style={{ ...bubbleSecondaryBtn, minWidth: 0 }}
                    >
                      Use Different Email
                    </button>
                  </div>
                )}
              </div>
            )}
            {emailVerified && (
              <>
                <input
                  type="password"
                  placeholder="Set password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={busy}
                  style={bubbleInputStyle}
                />
                <button
                  onClick={completeEmailSignup}
                  disabled={busy}
                  style={{ ...bubblePrimaryBtn, width: "min(100%, 320px)" }}
                >
                  {busy ? "Finalizing..." : "Save & Submit for Verification"}
                </button>
              </>
            )}
          </div>
        ) : (
          <div style={{ display: "grid", gap: 10, maxWidth: 640 }}>
            {isLocalhost && (
              <div style={{ border: "1px solid #f5d56c", background: "#fff8dc", color: "#8a5b00", borderRadius: 18, padding: 14 }}>
                Mobile OTP may fail on <strong>localhost</strong>. Use <strong>127.0.0.1</strong>.
                <div style={{ marginTop: 8 }}>
                  <button onClick={open127Host} style={{ ...bubbleSecondaryBtn, minWidth: 0 }}>
                    Open 127.0.0.1
                  </button>
                </div>
              </div>
            )}
            <input
              placeholder="Mobile (+91...)"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={busy}
              style={bubbleInputStyle}
            />
            <button
              onClick={sendPhoneOtp}
              disabled={sendingOtp || busy}
              style={{ ...bubblePrimaryBtn, width: "min(100%, 180px)" }}
            >
              {sendingOtp ? "Sending..." : "Send OTP"}
            </button>
            <input
              placeholder="Enter OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              disabled={busy}
              style={bubbleInputStyle}
            />
            <input
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
              style={bubbleInputStyle}
            />
            <input
              type="password"
              placeholder="Set password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
              style={bubbleInputStyle}
            />
            <button
              onClick={completePhoneSignup}
              disabled={busy}
              style={{ ...bubblePrimaryBtn, width: "min(100%, 330px)" }}
            >
              {busy ? "Submitting..." : "Verify OTP & Submit for Verification"}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderProviderWizard = () => {
    const stepTitleMap = {
      1: "Account Setup",
      2: "Personal Details",
      3: "Service Details",
    };
    const stepHint = `Step ${providerStep} of 3: ${stepTitleMap[providerStep]}`;
    const fieldBlockStyle = { display: "grid", gap: 6 };
    const wideCtaStyle = {
      ...bubblePrimaryBtn,
      width: "100%",
      maxWidth: 400,
      padding: "14px 20px",
      fontSize: 17,
    };

    return (
      <div
        style={{
          maxWidth: 1180,
          margin: "0 auto",
          padding: "6px 0 28px",
        }}
      >
        <PageHeader
          eyebrow="Partner Onboarding"
          title="Sign Up to Become a Petaar Partner"
          subtitle="Create your provider account, submit verification details, and start selling services on Petaar."
        />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: 24,
            alignItems: "start",
          }}
        >
          <div className="petaar-card" style={authCardStyle}>
            {providerStep > 1 && (
              <p style={{ marginTop: 0, marginBottom: 20, color: "#7b8494", fontSize: 14, fontWeight: 700 }}>
                {stepHint}
              </p>
            )}

            {providerStep === 1 && (
              <div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 18 }}>
              <div style={fieldBlockStyle}>
                <label style={bubbleLabelStyle}>Name</label>
                <input
                  placeholder="Please enter your full name"
                  value={partnerName}
                  onChange={(e) => setPartnerName(e.target.value)}
                  disabled={busy}
                  style={bubbleInputStyle}
                />
              </div>
              <div style={fieldBlockStyle}>
                <label style={bubbleLabelStyle}>Email</label>
                <input
                  placeholder="Please enter your valid email id"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={busy}
                  style={bubbleInputStyle}
                />
              </div>
              <div style={fieldBlockStyle}>
                <label style={bubbleLabelStyle}>What do you do?</label>
                <select
                  value={providerPrimaryCategory}
                  onChange={(e) => {
                    setProviderPrimaryCategory(e.target.value);
                    if (!serviceCategory) {
                      setServiceCategory(e.target.value);
                    }
                  }}
                  disabled={busy}
                  style={{
                    ...bubbleSelectStyle,
                    backgroundColor: "#f1f2f4",
                    border: "1px solid #a7adb8",
                  }}
                >
                  <option value="">Select service category</option>
                  {SERVICE_CATEGORIES.map((cat) => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginTop: 14, maxWidth: 360 }}>
              <label style={bubbleLabelStyle}>Contact Number(10 digits)</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 10 }}>
                <input
                  placeholder="+91 Enter mobile number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={busy}
                  style={bubbleInputStyle}
                />
                <button
                  type="button"
                  onClick={() => {
                    alert("OTP verification is done in the final step. Please complete this form and continue.");
                  }}
                  disabled={busy}
                  style={{
                    borderRadius: 4,
                    border: "none",
                    background: "#ef6d34",
                    color: "#fff",
                    fontWeight: 700,
                    padding: "0 20px",
                    minWidth: 122,
                    cursor: "pointer",
                  }}
                >
                  Send OTP
                </button>
              </div>
            </div>

            <label style={{ marginTop: 30, display: "flex", gap: 10, alignItems: "center", fontSize: 16, color: "#4b5563", fontWeight: 600 }}>
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                disabled={busy}
                style={{ width: 18, height: 18, accentColor: "#ef6c34", borderRadius: 0 }}
              />
              I have read all terms and conditions. I wish to register.
            </label>

            <div style={{ display: "flex", justifyContent: "center", marginTop: 34 }}>
              <button onClick={nextProviderStep} style={wideCtaStyle} disabled={busy}>
                Become a Partner
              </button>
            </div>

            <div style={{ marginTop: 16, display: "flex", justifyContent: "center" }}>
              <button
                onClick={goLogin}
                disabled={busy}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#2563eb",
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Back to Login
              </button>
            </div>
              </div>
            )}

            {providerStep === 2 && (
              <div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 18 }}>
              <div style={fieldBlockStyle}>
                <label style={bubbleLabelStyle}>City</label>
                <select value={providerCity} onChange={(e) => setProviderCity(e.target.value)} disabled={busy} style={bubbleSelectStyle}>
                  <option value="">Select your city</option>
                  {MAJOR_INDIAN_CITIES.map((cityName) => (
                    <option key={cityName} value={cityName}>{cityName}</option>
                  ))}
                </select>
              </div>
              <div style={fieldBlockStyle}>
                <label style={bubbleLabelStyle}>PAN Card Number</label>
                <input
                  placeholder="ABCDE1234F"
                  value={panCardNumber}
                  onChange={(e) => setPanCardNumber(e.target.value.toUpperCase())}
                  disabled={busy}
                  style={bubbleInputStyle}
                />
              </div>
              <div style={fieldBlockStyle}>
                <label style={bubbleLabelStyle}>ID Proof</label>
                <select value={idProofType} onChange={(e) => setIdProofType(e.target.value)} disabled={busy} style={bubbleSelectStyle}>
                  <option value="">Select ID proof</option>
                  {ID_PROOF_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div style={fieldBlockStyle}>
                <label style={bubbleLabelStyle}>ID Proof Number</label>
                <input
                  placeholder="Enter selected ID number"
                  value={idProofNumber}
                  onChange={(e) => setIdProofNumber(e.target.value)}
                  disabled={busy}
                  style={bubbleInputStyle}
                />
              </div>
            </div>
            <div style={{ marginTop: 16, display: "grid", gap: 18 }}>
              <div style={sectionPanelStyle}>
                <div style={{ fontWeight: 800, color: "#334155", marginBottom: 10 }}>Current Address</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
                  <div style={fieldBlockStyle}>
                    <label style={bubbleLabelStyle}>Address Line</label>
                    <input
                      placeholder="Flat/Street/Area"
                      value={currentAddressLine}
                      onChange={(e) => setCurrentAddressLine(e.target.value)}
                      disabled={busy}
                      style={bubbleInputStyle}
                    />
                  </div>
                  <div style={fieldBlockStyle}>
                    <label style={bubbleLabelStyle}>City</label>
                    <input
                      placeholder="City"
                      value={currentAddressCity}
                      onChange={(e) => setCurrentAddressCity(e.target.value)}
                      disabled={busy}
                      style={bubbleInputStyle}
                    />
                  </div>
                  <div style={fieldBlockStyle}>
                    <label style={bubbleLabelStyle}>Pincode</label>
                    <input
                      placeholder="6-digit pincode"
                      value={currentAddressPincode}
                      onChange={(e) => setCurrentAddressPincode(normalizePincode(e.target.value))}
                      disabled={busy}
                      style={bubbleInputStyle}
                    />
                  </div>
                  <div style={fieldBlockStyle}>
                    <label style={bubbleLabelStyle}>State (auto)</label>
                    <input
                      placeholder={currentAddressLookupBusy ? "Detecting state..." : "State"}
                      value={currentAddressState}
                      disabled
                      style={{ ...bubbleInputStyle, background: "#eef2f7" }}
                    />
                  </div>
                </div>
                {currentAddressLookupError && (
                  <small style={{ display: "block", marginTop: 8, color: "#b45309" }}>
                    {currentAddressLookupError}
                  </small>
                )}
              </div>

              <div style={sectionPanelStyle}>
                <div style={{ fontWeight: 800, color: "#334155", marginBottom: 10 }}>Permanent Address</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
                  <div style={fieldBlockStyle}>
                    <label style={bubbleLabelStyle}>Address Line</label>
                    <input
                      placeholder="Flat/Street/Area"
                      value={permanentAddressLine}
                      onChange={(e) => setPermanentAddressLine(e.target.value)}
                      disabled={busy}
                      style={bubbleInputStyle}
                    />
                  </div>
                  <div style={fieldBlockStyle}>
                    <label style={bubbleLabelStyle}>City</label>
                    <input
                      placeholder="City"
                      value={permanentAddressCity}
                      onChange={(e) => setPermanentAddressCity(e.target.value)}
                      disabled={busy}
                      style={bubbleInputStyle}
                    />
                  </div>
                  <div style={fieldBlockStyle}>
                    <label style={bubbleLabelStyle}>Pincode</label>
                    <input
                      placeholder="6-digit pincode"
                      value={permanentAddressPincode}
                      onChange={(e) => setPermanentAddressPincode(normalizePincode(e.target.value))}
                      disabled={busy}
                      style={bubbleInputStyle}
                    />
                  </div>
                  <div style={fieldBlockStyle}>
                    <label style={bubbleLabelStyle}>State (auto)</label>
                    <input
                      placeholder={permanentAddressLookupBusy ? "Detecting state..." : "State"}
                      value={permanentAddressState}
                      disabled
                      style={{ ...bubbleInputStyle, background: "#eef2f7" }}
                    />
                  </div>
                </div>
                {permanentAddressLookupError && (
                  <small style={{ display: "block", marginTop: 8, color: "#b45309" }}>
                    {permanentAddressLookupError}
                  </small>
                )}
              </div>
            </div>
            <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14 }}>
              <div style={fieldBlockStyle}>
                <label style={bubbleLabelStyle}>Profile Photo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => onProfilePhotoChange(e.target.files?.[0])}
                  disabled={busy}
                  style={{
                    ...bubbleInputStyle,
                    background: "#ffffff",
                    padding: 10,
                    borderStyle: "dashed",
                  }}
                />
                {profilePhotoFile && (
                  <small style={{ display: "block", marginTop: 6, color: "#475569" }}>
                    Selected: {profilePhotoFile.name}
                  </small>
                )}
              </div>
              <div style={fieldBlockStyle}>
                <label style={bubbleLabelStyle}>PAN Card Photo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => onDocumentPhotoChange(e.target.files?.[0], setPanCardPhotoFile)}
                  disabled={busy}
                  style={{
                    ...bubbleInputStyle,
                    background: "#ffffff",
                    padding: 10,
                    borderStyle: "dashed",
                  }}
                />
                {panCardPhotoFile && (
                  <small style={{ display: "block", marginTop: 6, color: "#475569" }}>
                    Selected: {panCardPhotoFile.name}
                  </small>
                )}
              </div>
              <div style={fieldBlockStyle}>
                <label style={bubbleLabelStyle}>ID Proof Photo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => onDocumentPhotoChange(e.target.files?.[0], setIdProofPhotoFile)}
                  disabled={busy}
                  style={{
                    ...bubbleInputStyle,
                    background: "#ffffff",
                    padding: 10,
                    borderStyle: "dashed",
                  }}
                />
                {idProofPhotoFile && (
                  <small style={{ display: "block", marginTop: 6, color: "#475569" }}>
                    Selected: {idProofPhotoFile.name}
                  </small>
                )}
              </div>
            </div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 24 }}>
              <button onClick={previousProviderStep} style={bubbleSecondaryBtn} disabled={busy}>
                Back
              </button>
              <button onClick={nextProviderStep} style={{ ...bubblePrimaryBtn, width: "min(100%, 220px)" }} disabled={busy}>
                Continue
              </button>
            </div>
              </div>
            )}

            {providerStep === 3 && (
              <div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 18 }}>
              <div style={fieldBlockStyle}>
                <label style={bubbleLabelStyle}>Service You Provide</label>
                <select value={serviceCategory} onChange={(e) => setServiceCategory(e.target.value)} disabled={busy} style={bubbleSelectStyle}>
                  <option value="">Select service</option>
                  {SERVICE_CATEGORIES.map((cat) => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div style={fieldBlockStyle}>
                <label style={bubbleLabelStyle}>Experience (years)</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={serviceExperience}
                  onChange={(e) => setServiceExperience(e.target.value)}
                  disabled={busy}
                  style={bubbleInputStyle}
                />
              </div>
              <div style={fieldBlockStyle}>
                <label style={bubbleLabelStyle}>Expertise</label>
                <select value={serviceExpertise} onChange={(e) => setServiceExpertise(e.target.value)} disabled={busy} style={bubbleSelectStyle}>
                  {EXPERTISE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div style={fieldBlockStyle}>
                <label style={bubbleLabelStyle}>Languages (comma separated)</label>
                <input
                  placeholder="English, Hindi, Kannada"
                  value={serviceLanguages}
                  onChange={(e) => setServiceLanguages(e.target.value)}
                  disabled={busy}
                  style={bubbleInputStyle}
                />
              </div>
            </div>
            <div style={{ marginTop: 16, ...fieldBlockStyle }}>
              <label style={bubbleLabelStyle}>About Yourself</label>
              <textarea
                placeholder="Tell pet parents about your background and approach"
                value={serviceAbout}
                onChange={(e) => setServiceAbout(e.target.value)}
                rows={4}
                disabled={busy}
                style={bubbleTextareaStyle}
              />
            </div>
            <div style={{ marginTop: 16, ...fieldBlockStyle }}>
              <label style={bubbleLabelStyle}>Service Photos</label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => onServicePhotosChange(e.target.files)}
                disabled={busy}
                style={{
                  ...bubbleInputStyle,
                  background: "#ffffff",
                  padding: 10,
                  borderStyle: "dashed",
                }}
              />
              {servicePhotoFiles.length > 0 && (
                <div style={{ marginTop: 8, display: "grid", gap: 6 }}>
                  {servicePhotoFiles.map((file, idx) => (
                    <div key={`${file.name}-${idx}`} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                      <small style={{ color: "#475569" }}>{file.name}</small>
                      <button
                        type="button"
                        onClick={() => removeServicePhotoAt(idx)}
                        disabled={busy}
                        style={{ ...bubbleSecondaryBtn, minWidth: 120, padding: "8px 12px" }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {renderVerificationBlock()}
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 22 }}>
              <button onClick={previousProviderStep} style={bubbleSecondaryBtn} disabled={busy}>
                Back
              </button>
            </div>
              </div>
            )}
          </div>

          <aside className="petaar-card" style={helperCardStyle}>
            <div>
              <ToneBadge tone="accent">3-step onboarding</ToneBadge>
              <h2 style={{ margin: "14px 0 10px", fontSize: "1.95rem", lineHeight: 1.1 }}>
                Verification, profile, and service setup in one flow.
              </h2>
              <p style={{ margin: 0, color: "var(--muted)", lineHeight: 1.7 }}>
                Admin review depends on complete KYC and clear service details. Incomplete profiles slow approval.
              </p>
            </div>

            <div style={sectionPanelStyle}>
              <div style={{ fontWeight: 800, marginBottom: 10 }}>Progress</div>
              <div style={{ display: "grid", gap: 10 }}>
                {[
                  { step: 1, label: "Account setup" },
                  { step: 2, label: "Personal details" },
                  { step: 3, label: "Service details + verification" },
                ].map((item) => (
                  <div key={item.step} style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                    <span>{item.label}</span>
                    <ToneBadge tone={providerStep === item.step ? "accent" : providerStep > item.step ? "success" : "default"}>
                      {providerStep > item.step ? "Done" : providerStep === item.step ? "Current" : "Pending"}
                    </ToneBadge>
                  </div>
                ))}
              </div>
            </div>

            <div style={sectionPanelStyle}>
              <div style={{ fontWeight: 800, marginBottom: 10 }}>What improves approval</div>
              <div style={{ display: "grid", gap: 10, color: "var(--text)" }}>
                <div>• Clear profile photo and document uploads</div>
                <div>• Accurate address and registration details</div>
                <div>• Real service images and useful bio</div>
                <div>• Valid contact details for follow-up</div>
              </div>
            </div>

            <div style={sectionPanelStyle}>
              <div style={{ fontWeight: 800, marginBottom: 8 }}>Account mode</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <ToneBadge tone="default">Email/password</ToneBadge>
                <ToneBadge tone="warning">Phone OTP</ToneBadge>
                <ToneBadge tone="success">Verification required</ToneBadge>
              </div>
            </div>
          </aside>
        </div>
      </div>
    );
  };

  const renderOwnerSignup = () => {
    const ownerPrimaryBtn = {
      ...bubblePrimaryBtn,
      background: "#ef6d34",
      boxShadow: "0 8px 16px rgba(239, 109, 52, 0.25)",
    };
    const ownerPillBtn = (active) => ({
      ...bubbleSecondaryBtn,
      minWidth: 0,
      borderColor: active ? "#ef6d34" : "#cbd5e1",
      color: active ? "#9a3412" : "#475569",
      background: active ? "#ffedd5" : "#fff",
    });

    return (
      <div
        style={{
          maxWidth: 1180,
          margin: "0 auto",
          padding: "6px 0 28px",
        }}
      >
        <PageHeader
          eyebrow="Owner Onboarding"
          title="Sign Up as a Pet Owner"
          subtitle="Create your account with email or mobile OTP, then manage pets, bookings, and consultations in one place."
        />

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: 24,
            alignItems: "start",
          }}
        >
          <div className="petaar-card" style={authCardStyle}>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18 }}>
              <button onClick={() => setMode("email")} disabled={mode === "email" || busy} style={ownerPillBtn(mode === "email")}>
                Email Signup
              </button>
              <button onClick={() => setMode("phone")} disabled={mode === "phone" || busy} style={ownerPillBtn(mode === "phone")}>
                Mobile Signup
              </button>
            </div>

            {mode === "email" ? (
              <div style={{ display: "grid", gap: 12, maxWidth: 560 }}>
            <div style={{ display: "grid", gap: 6 }}>
              <label style={bubbleLabelStyle}>Email</label>
              <input
                placeholder="Please enter your valid email id"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={busy || emailVerificationSent}
                style={bubbleInputStyle}
              />
            </div>
            {!emailVerificationSent ? (
              <button
                onClick={sendEmailOtp}
                disabled={busy || !normalizedEmail}
                style={{ ...ownerPrimaryBtn, width: "min(100%, 200px)" }}
              >
                {busy ? "Sending..." : "Send Email OTP"}
              </button>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {!emailVerified && (
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button
                      onClick={checkEmailVerification}
                      disabled={busy}
                      style={{ ...ownerPrimaryBtn, width: "min(100%, 180px)" }}
                    >
                      {busy ? "Checking..." : "I Verified Email"}
                    </button>
                    <button onClick={resendEmailOtp} disabled={busy} style={bubbleSecondaryBtn}>
                      Resend OTP
                    </button>
                  </div>
                )}
                {emailVerified && (
                  <>
                    <div style={{ display: "grid", gap: 6 }}>
                      <label style={bubbleLabelStyle}>Set Password</label>
                      <input
                        type="password"
                        placeholder="Set password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={busy}
                        style={bubbleInputStyle}
                      />
                    </div>
                    <button onClick={completeEmailSignup} disabled={busy} style={{ ...ownerPrimaryBtn, width: "min(100%, 240px)" }}>
                      {busy ? "Finalizing..." : "Complete Signup"}
                    </button>
                  </>
                )}
              </div>
            )}
              </div>
            ) : (
              <div style={{ display: "grid", gap: 12, maxWidth: 560 }}>
            {isLocalhost && (
              <div
                style={{
                  border: "1px solid #f5d56c",
                  background: "#fff8dc",
                  color: "#8a5b00",
                  borderRadius: 18,
                  padding: 14,
                }}
              >
                Mobile OTP may fail on <strong>localhost</strong>. Use <strong>127.0.0.1</strong>.
                <div style={{ marginTop: 6 }}>
                  <button onClick={open127Host} style={bubbleSecondaryBtn}>Open 127.0.0.1</button>
                </div>
              </div>
            )}
            <div style={{ display: "grid", gap: 6 }}>
              <label style={bubbleLabelStyle}>Mobile (+91...)</label>
              <input
                placeholder="+91 Enter mobile number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={busy}
                style={bubbleInputStyle}
              />
            </div>
            <button onClick={sendPhoneOtp} disabled={sendingOtp || busy} style={{ ...ownerPrimaryBtn, width: "min(100%, 180px)" }}>
              {sendingOtp ? "Sending..." : "Send OTP"}
            </button>
            <div style={{ display: "grid", gap: 6 }}>
              <label style={bubbleLabelStyle}>Enter OTP</label>
              <input
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                disabled={busy}
                style={bubbleInputStyle}
              />
            </div>
            <div style={{ display: "grid", gap: 6 }}>
              <label style={bubbleLabelStyle}>Email</label>
              <input
                placeholder="Email (required for password login)"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={busy}
                style={bubbleInputStyle}
              />
            </div>
            <div style={{ display: "grid", gap: 6 }}>
              <label style={bubbleLabelStyle}>Set Password</label>
              <input
                type="password"
                placeholder="Set password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
                style={bubbleInputStyle}
              />
            </div>
            <button onClick={completePhoneSignup} disabled={busy} style={{ ...ownerPrimaryBtn, width: "min(100%, 300px)" }}>
              {busy ? "Verifying..." : "Verify OTP & Complete Signup"}
            </button>
              </div>
            )}

            <div style={{ marginTop: 18 }}>
              <button
                onClick={goLogin}
                disabled={busy}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#2563eb",
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontWeight: 700,
                  padding: 0,
                }}
              >
                Back to Login
              </button>
            </div>
          </div>

          <aside className="petaar-card" style={helperCardStyle}>
            <div>
              <ToneBadge tone="accent">Fast account setup</ToneBadge>
              <h2 style={{ margin: "14px 0 10px", fontSize: "1.95rem", lineHeight: 1.1 }}>
                Start booking pet care without friction.
              </h2>
              <p style={{ margin: 0, color: "var(--muted)", lineHeight: 1.7 }}>
                Use email for the cleanest long-term login flow. Use OTP when you need mobile-first access or recovery.
              </p>
            </div>

            <div style={sectionPanelStyle}>
              <div style={{ fontWeight: 800, marginBottom: 10 }}>After signup</div>
              <div style={{ display: "grid", gap: 10 }}>
                <div>• Add pet profiles and medical context</div>
                <div>• Track bookings and payment history</div>
                <div>• Start chat, audio, or video vet consults</div>
                <div>• Save providers and repeat services</div>
              </div>
            </div>

            <div style={sectionPanelStyle}>
              <div style={{ fontWeight: 800, marginBottom: 8 }}>Access options</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <ToneBadge tone={mode === "email" ? "accent" : "default"}>Email flow</ToneBadge>
                <ToneBadge tone={mode === "phone" ? "warning" : "default"}>OTP flow</ToneBadge>
                <ToneBadge tone="success">Secure verification</ToneBadge>
              </div>
            </div>
          </aside>
        </div>
      </div>
    );
  };

  return (
    <div className="petaar-page" style={{ paddingTop: 28 }}>
      <div style={{ maxWidth: 1180, margin: "0 auto 16px", display: "flex", justifyContent: "flex-start", gap: 10 }}>
        <button
          onClick={() => setRole("owner")}
          disabled={busy}
          style={{
            ...bubbleSecondaryBtn,
            minWidth: 0,
            borderColor: role === "owner" ? "#ef6d34" : "#cbd5e1",
            background: role === "owner" ? "#ffedd5" : "#fff",
            color: role === "owner" ? "#9a3412" : "#475569",
          }}
        >
          Pet Owner
        </button>
        <button
          onClick={() => setRole("provider")}
          disabled={busy}
          style={{
            ...bubbleSecondaryBtn,
            minWidth: 0,
            borderColor: role === "provider" ? "#4f46e5" : "#cbd5e1",
            background: role === "provider" ? "#e0e7ff" : "#fff",
            color: role === "provider" ? "#3730a3" : "#475569",
          }}
        >
          Pet Partner
        </button>
      </div>

      {isProvider ? renderProviderWizard() : renderOwnerSignup()}

      <div
        id="signup-recaptcha"
        style={{
          marginTop: 8,
          visibility: mode === "phone" ? "visible" : "hidden",
          height: mode === "phone" ? "auto" : 0,
          overflow: "hidden",
        }}
      />
    </div>
  );
}
